import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  debounce,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
} from '@mui/x-data-grid-premium';
import { ChevronDown, ListX, Search, Sparkles, Users, X } from 'lucide-react';
import type { Map } from 'mapbox-gl';
import { useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import {
  Place_Type_Enum_Enum,
  type SearchPlaceFragmentFragment,
} from '@graphql/__generated__/graphql';
import { RaNumber } from '@modules/_shared/components/form/RaNumber';
import {
  LocationMarker as LocationMarkerTS,
  Mapbox as MapboxTS,
} from '@modules/_shared/components/Mapbox';
import { formatNumber } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { cssTokens, customPalette } from '@shared/theme/StyleProvider';

export const PLACE_FRAGMENT = gql(/* GraphQL */ `
  fragment SearchPlaceFragment on places {
    id
    name
    lat
    lng
    label
    label_en
    population
    type
    state {
      id
      name
    }
  }
`);

const SEARCH_PLACES = gql(/* GraphQL */ `
  query SearchPlaces(
    $q: String!
    $placeTypes: [place_type_enum_enum!]!
    $selectedIds: [uuid!]
  ) {
    search_places(
      args: { search: $q }
      where: { type: { _in: $placeTypes }, id: { _nin: $selectedIds } }
      order_by: { population: desc_nulls_last }
      limit: 20
    ) {
      id
      ...SearchPlaceFragment
    }
  }
`);

const GET_DEFAULT_PLACES = gql(/* GraphQL */ `
  query GetPlacesByIds($ids: [uuid!]!) {
    places(where: { id: { _in: $ids } }, order_by: { label: asc }) {
      id
      ...SearchPlaceFragment
    }
  }
`);

const GET_CATCHMENT_AREA = gql(/* GraphQL */ `
  query AutoCatchmentArea($x: float8!, $y: float8!, $target_population: Int!) {
    get_catchment_area(
      args: { x: $x, y: $y, target_population: $target_population }
    ) {
      ...SearchPlaceFragment
    }
  }
`);

const GET_PLACE_BY_ID = gql(/* GraphQL */ `
  query GetPlaceById($id: uuid!) {
    places_by_pk(id: $id) {
      id
      ...SearchPlaceFragment
    }
  }
`);

interface PlaceAutocompleteProps {
  onSelect?: (place: SearchPlaceFragmentFragment) => void;
  placeTypes?: PlaceType[];
  selectedIds?: string[];
}

const PlaceAutocomplete = ({
  onSelect,
  placeTypes = [
    Place_Type_Enum_Enum.Locality,
    Place_Type_Enum_Enum.Municipality,
    Place_Type_Enum_Enum.District,
    Place_Type_Enum_Enum.State,
  ],
  selectedIds = [],
}: PlaceAutocompleteProps) => {
  const { t, locale } = useLocale();
  const [options, setOptions] = useState<SearchPlaceFragmentFragment[]>([]);
  const [searchPlaces, { loading }] = useLazyQuery(SEARCH_PLACES, {
    notifyOnNetworkStatusChange: true,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (q: string) =>
          searchPlaces({
            variables: {
              q: `%${q}%`,
              placeTypes,
              selectedIds: selectedIds.length > 0 ? selectedIds : [],
            },
          }).then(({ data }) => {
            setOptions(data?.search_places || []);
          }),
        500,
      ),
    [searchPlaces, placeTypes, selectedIds],
  );

  const handleSearch = (value: string) => {
    if (value) {
      debouncedSearch(value);
    } else {
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      size="small"
      blurOnSelect
      clearOnBlur
      clearOnEscape
      options={options}
      value={null}
      popupIcon={null}
      onOpen={() => setOptions([])}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={t('Search places...')}
          variant="outlined"
          sx={{
            background: 'var(--mui-palette-background-paper)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
          }}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              startAdornment: <Search size={16} style={{ marginRight: 6 }} />,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },

            htmlInput: {
              ...params.slotProps.htmlInput,
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
            },
          }}
        />
      )}
      onInputChange={(_, value) => handleSearch(value)}
      onChange={(_, value) => {
        if (!value) {
          return;
        }
        setOptions([]);
        onSelect?.(value);
      }}
      getOptionLabel={option => option.label || option.label_en || option.name}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id} sx={{ py: 0.5 }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  {option.label || option.label_en || option.name}
                </Box>
                {option.population && (
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                      {formatNumber(option.population, locale)}
                    </Typography>
                    <Users
                      size={14}
                      style={{ color: customPalette.disabledText }}
                    />
                  </Stack>
                )}
              </Box>
            }
            secondary={option.type}
            slotProps={{
              secondary: { sx: { fontSize: '0.75rem' } },
            }}
          />
        </ListItem>
      )}
    />
  );
};

interface PlacePickerProps {
  defaultValue: any;
  onChange: (value: any) => void;
  placeTypes?: Place_Type_Enum_Enum[];
  fullHeight?: boolean;
  condensed?: boolean;
}

type PlaceType = Place_Type_Enum_Enum;

type Place = Omit<SearchPlaceFragmentFragment, 'label_en' | 'name'> & {
  isLoading: boolean;
};

interface PlaceState {
  mapReady?: boolean;
  places: Place[];
  activeType: Place_Type_Enum_Enum;
}

type PlaceAction =
  | { type: 'SET_PLACES'; places: Place[] }
  | { type: 'TOGGLE_PLACE'; place: Place }
  | { type: 'SET_MAP_READY'; mapReady: boolean }
  | { type: 'SET_ACTIVE_TYPE'; activeType: Place_Type_Enum_Enum };

const placeReducer = (state: PlaceState, action: PlaceAction): PlaceState => {
  switch (action.type) {
    case 'SET_PLACES':
      return { ...state, places: action.places };
    case 'TOGGLE_PLACE': {
      const exists = state.places.some(p => p.id === action.place.id);
      let newPlaces;
      if (exists) {
        newPlaces = state.places.filter(p => p.id !== action.place.id);
      } else {
        newPlaces = [action.place, ...state.places];
      }
      return { ...state, places: newPlaces };
    }
    case 'SET_MAP_READY':
      return { ...state, mapReady: action.mapReady };
    case 'SET_ACTIVE_TYPE':
      return { ...state, activeType: action.activeType };
    default:
      return state;
  }
};

interface AutoFormData {
  targetPopulation: number;
}

export const PlacePicker = ({
  defaultValue,
  onChange,
  placeTypes = [
    Place_Type_Enum_Enum.Locality,
    Place_Type_Enum_Enum.Municipality,
    Place_Type_Enum_Enum.District,
    Place_Type_Enum_Enum.State,
  ],
  fullHeight = true,
  condensed = false,
}: PlacePickerProps) => {
  const { t } = useLocale();
  const [{ places, mapReady, activeType }, dispatch] = useReducer(
    placeReducer,
    {
      places: [],
      mapReady: false,
      activeType: Place_Type_Enum_Enum.Locality,
    },
  );
  const [getDefaultPlaces, { loading }] = useLazyQuery(GET_DEFAULT_PLACES, {
    notifyOnNetworkStatusChange: true,
  });
  const [getCatchmentArea, { loading: loadingCatchment }] = useLazyQuery(
    GET_CATCHMENT_AREA,
    { notifyOnNetworkStatusChange: true },
  );
  const [isAutoDialogOpen, setIsAutoDialogOpen] = useState(false);
  const [getPlaceById] = useLazyQuery(GET_PLACE_BY_ID);

  const { control, handleSubmit } = useForm<AutoFormData>({
    defaultValues: {
      targetPopulation: 100000,
    },
  });

  const layers: Partial<
    Record<
      Place_Type_Enum_Enum,
      { label: string; tilesetId: string; sourceLayer: string }
    >
  > = useMemo(
    () => ({
      locality: {
        label: t('Locality'),
        tilesetId: 'spingwun.crm-locality-poly-dev',
        sourceLayer: 'crm-locality-poly-dev',
      },
      municipality: {
        label: t('Municipality'),
        tilesetId: 'spingwun.crm-municipality-poly-dev',
        sourceLayer: 'crm-municipality-poly-dev',
      },
      district: {
        label: t('District'),
        tilesetId: 'spingwun.crm-district-poly-dev',
        sourceLayer: 'crm-district-poly-dev',
      },
      state: {
        label: t('State'),
        tilesetId: 'spingwun.crm-state-poly-dev',
        sourceLayer: 'crm-state-poly-dev',
      },
    }),
    [t],
  );

  const placesRef = useRef(places);
  useEffect(() => {
    placesRef.current = places;
  }, [places]);

  useEffect(() => {
    if (!defaultValue || defaultValue.length === 0) {
      return;
    }
    getDefaultPlaces({
      variables: { ids: defaultValue },
    }).then(({ data }) => {
      const places = (data?.places || []).map(p => ({
        ...p,
        label: p.label || p.label_en || p.name,
        isLoading: false,
      })) as Place[];
      dispatch({ type: 'SET_PLACES', places });
      updateBounds(places);
    });
  }, [defaultValue, getDefaultPlaces]);

  const updateBounds = (places: Place[]) => {
    if (!mapbox.current || places.length === 0) {
      return;
    }
    const minLat = Math.min(...places.map(p => p.lat));
    const maxLat = Math.max(...places.map(p => p.lat));
    const minLng = Math.min(...places.map(p => p.lng));
    const maxLng = Math.max(...places.map(p => p.lng));
    mapbox.current?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 100, duration: 250, maxZoom: 12 },
    );
  };

  const updatePlace = useCallback(
    (
      id: string,
      placeData: Partial<SearchPlaceFragmentFragment>,
      isLoading = false,
    ) => {
      const newPlace = {
        id,
        lat: placeData.lat || 0,
        lng: placeData.lng || 0,
        label: placeData.label || placeData.label_en || placeData.name || '',
        population: placeData.population || undefined,
        type: activeType,
        state: placeData.state,
        isLoading,
      };

      dispatch({
        type: 'SET_PLACES',
        places: placesRef.current.some(p => p.id === id)
          ? placesRef.current.map(p => (p.id === id ? newPlace : p))
          : [newPlace, ...placesRef.current],
      });
    },
    [activeType],
  );

  const mapbox = useRef<Map | null>(null);
  const defaultIds = useMemo(
    () => defaultValue?.map((id: string) => id) ?? [],
    [defaultValue],
  );

  const onRemove = useCallback(
    (place: Place) => {
      const newPlaces = places.filter(p => p.id !== place.id);
      dispatch({ type: 'SET_PLACES', places: newPlaces });
      updateBounds(newPlaces);
      onChange(newPlaces.map(p => p.id));
    },
    [places, onChange],
  );

  useEffect(() => {
    if (!mapbox) {
      return;
    } // no map
    if (!mapbox.current) {
      return;
    } // wait for map to initialize
    mapbox.current.on('load', () => {
      Object.entries(layers).forEach(([key, value]) => {
        mapbox.current?.addSource(key, {
          type: 'vector',
          url: `mapbox://${value.tilesetId}`,
        });
      });

      Object.entries(layers).forEach(([key, value]) => {
        mapbox.current?.addLayer({
          id: `${key}-fill`,
          type: 'fill',
          source: key,
          'source-layer': value.sourceLayer,
          paint: {
            'fill-color': '#2196F3',
            'fill-opacity': 0.25,
          },
          filter: ['in', 'id', ...defaultIds],
        });
      });

      mapbox.current?.on('click', 'clickable', async e => {
        const mapBoxPlace: any = e.features?.[0].properties;
        const { id, label_en: label, lat, lng } = mapBoxPlace;

        const isAlreadySelected = placesRef.current.some(p => p.id === id);

        if (isAlreadySelected) {
          const newPlaces = placesRef.current.filter(p => p.id !== id);
          dispatch({ type: 'SET_PLACES', places: newPlaces });
          updateBounds(newPlaces);
          onChange(newPlaces.map(p => p.id));
          return;
        }

        // Add initial place with loading state
        updatePlace(
          id,
          { id, label, lat, lng, population: undefined, state: undefined },
          true,
        );

        try {
          const { data } = await getPlaceById({ variables: { id } });
          if (data?.places_by_pk) {
            updatePlace(id, data.places_by_pk);
          }
        } catch (error) {
          // On error, keep existing data but remove loading state
          updatePlace(id, placesRef.current.find(p => p.id === id) || {});
        }
      });

      dispatch({ type: 'SET_MAP_READY', mapReady: true });
    });
  }, [defaultIds, layers, activeType, getPlaceById, updatePlace, onChange]);

  useEffect(() => {
    if (!mapbox.current || !mapReady) {
      return;
    }

    if (mapbox.current.getLayer('boundaries')) {
      mapbox.current.removeLayer('boundaries');
    }

    const layer = layers[activeType];
    if (layer) {
      mapbox.current?.addLayer({
        id: 'boundaries',
        type: 'line',
        source: activeType,
        'source-layer': layer.sourceLayer,
        paint: {
          'line-color': '#2196F3',
          'line-width': 1,
        },
      });
    }

    if (mapbox.current.getLayer('clickable')) {
      mapbox.current.removeLayer('clickable');
    }

    if (layer) {
      mapbox.current?.addLayer({
        id: 'clickable',
        type: 'fill',
        source: activeType,
        'source-layer': layer.sourceLayer,
        paint: {
          'fill-opacity': 0,
        },
      });
    }
  }, [mapbox, mapReady, activeType, layers]);

  useEffect(() => {
    if (!mapbox.current || !mapReady) {
      return;
    }

    Object.entries(layers).forEach(([key]) => {
      mapbox.current?.setFilter(`${key}-fill`, [
        'in',
        'id',
        ...places.map(p => p.id),
      ]);
    });

    onChange(places.map(p => p.id));

    // Fit map to bounds
  }, [places, mapReady, onChange, layers]);

  const handleAutoClick = useCallback(
    async (data: AutoFormData) => {
      if (!mapbox.current) {
        return;
      }

      const center = mapbox.current.getCenter();
      const { data: response } = await getCatchmentArea({
        variables: {
          x: center.lng,
          y: center.lat,
          target_population: data.targetPopulation,
        },
      });

      if (response?.get_catchment_area) {
        const newPlaces = response.get_catchment_area.map(
          (place: SearchPlaceFragmentFragment) => ({
            id: place.id,
            label: place.label || place.label_en || place.name,
            lat: place.lat,
            lng: place.lng,
            population: place.population || undefined,
            type: place.type,
            state: place.state,
            isLoading: false,
          }),
        );
        dispatch({ type: 'SET_PLACES', places: newPlaces });
        updateBounds(newPlaces);
        onChange(newPlaces.map((p: Place) => p.id));
      }
      setIsAutoDialogOpen(false);
    },
    [getCatchmentArea, onChange],
  );

  const handlePlaceSelect = useCallback(
    (place: SearchPlaceFragmentFragment) => {
      updatePlace(place.id, place);
      updateBounds([...places, { ...place, isLoading: false }]);
    },
    [places, updatePlace],
  );

  const columns = useMemo(() => {
    const cols: GridColDef<Place>[] = [
      {
        field: 'label',
        headerName: t('Name'),
        flex: 2,
        display: 'flex',
        renderCell: condensed
          ? params => (
              <Stack direction="column">
                <Typography variant="body2" noWrap>
                  {params.row.label}
                </Typography>
                {params.row.state && (
                  <Typography variant="caption" noWrap>
                    {params.row.state.name}
                  </Typography>
                )}
              </Stack>
            )
          : undefined,
      },
      {
        field: 'type',
        headerName: t('Type'),
        flex: 1,
        valueGetter: (_, row) => {
          switch (row.type) {
            case 'locality':
              return t('Locality');
            case 'municipality':
              return t('Municipality');
            case 'district':
              return t('District');
            case 'state':
              return t('State');
            default:
              return row.type;
          }
        },
      },
    ];

    if (!condensed) {
      cols.push(
        {
          field: 'state',
          headerName: t('State'),
          flex: 1,
          renderCell: params =>
            params.row.isLoading ? (
              <Skeleton animation="wave" />
            ) : (
              params.row.state?.name || '-'
            ),
        },
        {
          field: 'population',
          headerName: t('Population'),
          type: 'number',
          flex: 1,
          renderCell: params =>
            params.row.isLoading ? (
              <Skeleton animation="wave" />
            ) : (
              params.row.population
            ),
        },
      );
    }

    cols.push({
      field: 'actions',
      type: 'actions',
      getActions: params => [
        <GridActionsCellItem
          icon={
            params.id === 'auto-generated-group-footer-root' ? (
              <Tooltip title={t('Remove all')}>
                <ListX size={18} />
              </Tooltip>
            ) : (
              <X size={18} />
            )
          }
          label={t('Remove')}
          onClick={() => {
            if (params.id === 'auto-generated-group-footer-root') {
              dispatch({ type: 'SET_PLACES', places: [] });
            } else {
              onRemove(params.row as Place);
            }
          }}
        />,
      ],
      width: condensed ? 30 : 50,
    });

    return cols;
  }, [t, condensed, onRemove]);

  return (
    <>
      <Stack
        direction={{
          xs: 'column-reverse',
          sm: condensed ? 'row' : 'column-reverse',
          md: 'row',
        }}
        sx={{
          gap: 2,

          height: {
            xs: fullHeight ? '100%' : 550,
            sm: fullHeight ? '100%' : 450,
          },
        }}
      >
        {/* Left panel: search + grid */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            order: { xs: 2, sm: condensed ? 1 : 2, md: 1 },
          }}
        >
          <PlaceAutocomplete
            placeTypes={placeTypes}
            selectedIds={places.map(p => p.id)}
            onSelect={handlePlaceSelect}
          />
          <Box sx={{ flexGrow: 1, mt: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <DataGridPremium
                slotProps={{
                  loadingOverlay: {
                    variant: 'skeleton',
                    noRowsVariant: 'skeleton',
                  },
                }}
                loading={loading || loadingCatchment}
                density={condensed ? 'standard' : 'compact'}
                rows={places}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'label', sort: 'asc' }],
                  },
                }}
                columns={columns}
                aggregationModel={{ population: 'sum' }}
                disableColumnMenu
                disableRowSelectionOnClick
                disableColumnReorder
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: '8px',
                  '& .MuiDataGrid-columnHeaders': {
                    fontSize: '0.75rem',
                  },
                  '& .MuiDataGrid-aggregationColumnHeaderLabel': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-filler': {
                    background: cssTokens.grey50,
                  },
                  '& .MuiDataGrid-overlay': {
                    background: cssTokens.grey50,
                  },
                  '& .MuiDataGrid-footerContainer': {
                    fontSize: '0.75rem',
                  },
                }}
                hideFooter={places.length === 0}
                disableAggregation={places.length === 0}
              />
            </Box>
          </Box>
        </Box>

        {/* Right panel: map */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            borderRadius: '10px',
            overflow: 'hidden',
            order: { xs: 1, sm: condensed ? 2 : 1, md: 2 },
          }}
        >
          <MapboxTS mapRef={mapbox}>
            {places.map(place => (
              <LocationMarkerTS
                key={place.id}
                lat={place.lat}
                lng={place.lng}
              />
            ))}
          </MapboxTS>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsAutoDialogOpen(true)}
              disabled={!mapReady || loadingCatchment}
              startIcon={<Sparkles size={16} />}
              sx={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                gap: '4px',
              }}
            >
              {t('Auto')}
            </Button>
          </Box>
          {placeTypes.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
              }}
            >
              <Select
                variant="outlined"
                size="small"
                IconComponent={({ className }) => (
                  <ChevronDown size={16} className={className} />
                )}
                sx={{
                  minWidth: 160,
                  background: 'var(--mui-palette-background-paper)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.200',
                  },
                  '& .MuiSelect-icon': {
                    right: 8,
                  },
                }}
                value={activeType}
                onChange={e =>
                  dispatch({
                    type: 'SET_ACTIVE_TYPE',
                    activeType: e.target.value as PlaceType,
                  })
                }
              >
                {Object.entries(layers).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      </Stack>
      <Dialog
        open={isAutoDialogOpen}
        onClose={() => setIsAutoDialogOpen(false)}
        slotProps={{
          paper: { sx: { borderRadius: '10px' } },
        }}
      >
        <form onSubmit={handleSubmit(handleAutoClick)}>
          <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Sparkles size={18} />
              {t('Target Population')}
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <RaNumber
                control={control}
                name="targetPopulation"
                label={t('Population')}
                required
                min={1}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setIsAutoDialogOpen(false)}
              sx={{
                color: 'text.primary',
              }}
            >
              {t('Cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loadingCatchment}
              disableElevation
            >
              {t('Generate')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
